import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/frontend-defaults';
import {
  convertLegacyAppOptions,
  convertLegacyAppRoot,
} from '@backstage/core-compat-api';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import authPlugin from '@backstage/plugin-auth';

// MINTEL CUSTOM
import { TechRadarPage } from '@backstage-community/plugin-tech-radar';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';
import { CatalogValidationPage } from './components/catalog/CatalogValidationPage';
import { SortedEntityPickerFieldExtension } from './scaffolder/SortedEntityPicker';
import { LlmProxyKeysPage } from '@internal/plugin-llm-proxy-keys';

// END MINTEL CUSTOM

const CatalogImportPageWithPermission = () => (
  <RequirePermission permission={catalogEntityCreatePermission}>
    <CatalogImportPage />
  </RequirePermission>
);

const convertedOptionsModule = convertLegacyAppOptions({
  apis,
  components: {
    SignInPage: props => {
      const signInProviders =
        process.env.NODE_ENV === 'development'
          ? [
              {
                id: 'entra-id',
                title: 'Microsoft Entra ID',
                message: 'Sign in using Microsoft Entra ID',
                apiRef: microsoftAuthApiRef,
              },
              'guest' as const,
            ]
          : [
              {
                id: 'entra-id',
                title: 'Microsoft Entra ID',
                message: 'Sign in using Microsoft Entra ID',
                apiRef: microsoftAuthApiRef,
              },
            ];

      return (
        <SignInPage {...props} auto providers={signInProviders} />
      );
    },
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />}>
      <ScaffolderFieldExtensions>
        <SortedEntityPickerFieldExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={<CatalogImportPageWithPermission />}
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />

    <Route
      path="/tech-radar"
      element={<TechRadarPage width={1500} height={800} />}
    />
    <Route path="/catalog-validation" element={<CatalogValidationPage />} />
    <Route
      path="/ai-platform/llm-proxy-localdev/virtual-keys"
      element={<LlmProxyKeysPage />}
    />
    <Route
      path="/ai-platform/llm-proxy-apps/virtual-keys"
      element={<LlmProxyKeysPage />}
    />
  </FlatRoutes>
);

const convertedRootFeatures = convertLegacyAppRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
  { entityPage },
);

const app = createApp({
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: undefined,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  features: [authPlugin, convertedOptionsModule, ...convertedRootFeatures],
});

export default app.createRoot();
